import * as d3 from 'd3';

import {
  IndexedTsPoint,
  SpxTsData,
} from '../../charts/band-chart/BandChart.types';
import { ChartXAxisCalendarStep } from './ChartXAxis';

const MIN_X_PADDING = 10;
const MONTH_STEP = 1;

const getCalendarDatesRange = (
  start: Date,
  end: Date,
  calendarStep: ChartXAxisCalendarStep
): Date[] => {
  if (calendarStep === 'week') {
    const weekStart = d3.timeMonday.floor(start);
    const weekStop = d3.timeMonday.offset(d3.timeMonday.ceil(end), 1);
    return d3.timeMonday.range(weekStart, weekStop);
  }

  return d3.timeMonths(start, end, MONTH_STEP);
};

const reduceDateRange = (
  datesRange: Date[],
  dateScale: d3.ScaleTime<number, number>
): Date[] => {
  const [minX, maxX] = dateScale.range();

  return datesRange.reduce<Date[]>((dateRange, date) => {
    const x = dateScale(date);

    if (minX + x <= MIN_X_PADDING || maxX - x <= MIN_X_PADDING) {
      return dateRange;
    }

    return [...dateRange, date];
  }, []);
};

export interface ChartXAxisTicksParams {
  tsData: IndexedTsPoint[] | SpxTsData[];
  /** Pixel range the ticks are mapped onto, i.e. the chart's x-scale range. */
  xRange: number[];
  domainLimited?: boolean;
  calendarStep?: ChartXAxisCalendarStep;
}

export interface ChartXAxisTicks {
  tickValues: Date[];
  dateScale: d3.ScaleTime<number, number>;
}

export const getChartXAxisDateDomain = (
  tsData: IndexedTsPoint[] | SpxTsData[],
  domainLimited = false
): [Date, Date] | null => {
  if (!tsData.length) {
    return null;
  }

  const lastDate = tsData[tsData.length - 1].date;
  const sixMonthStart = new Date(lastDate);
  sixMonthStart.setMonth(sixMonthStart.getMonth() - 6);

  return domainLimited ? [sixMonthStart, lastDate] : [tsData[0].date, lastDate];
};

export const createChartXAxisDateScale = (
  domain: [Date, Date] | null,
  xRange: number[]
): d3.ScaleTime<number, number> => {
  if (!domain) {
    return d3.scaleTime().range(xRange);
  }

  return d3.scaleTime().domain(domain).range(xRange);
};

export const getChartXAxisTickValues = (
  dateScale: d3.ScaleTime<number, number>,
  calendarStep: ChartXAxisCalendarStep = 'month'
): Date[] => {
  const domain = dateScale.domain();

  if (domain.length < 2) {
    return [];
  }

  const datesRange = getCalendarDatesRange(domain[0], domain[1], calendarStep);

  return reduceDateRange(datesRange, dateScale);
};

/**
 * Computes calendar tick dates (and the time scale that positions them) for
 * `ChartXAxis`. Exported for charts whose layout depends on tick presence
 * before render (empty `tickValues` === no axis). Simple consumers can pass
 * `tsData` + `xRange` to `ChartXAxis` instead.
 */
export const getChartXAxisTicks = ({
  tsData,
  xRange,
  domainLimited = false,
  calendarStep = 'month',
}: ChartXAxisTicksParams): ChartXAxisTicks => {
  const dateScale = createChartXAxisDateScale(
    getChartXAxisDateDomain(tsData, domainLimited),
    xRange
  );

  return {
    tickValues: getChartXAxisTickValues(dateScale, calendarStep),
    dateScale,
  };
};
